import type { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { TCampaignGroup } from '@feathr/blackbox';
import { CampaignClass, CampaignGroupMap, EUsageMetrics } from '@feathr/blackbox';
import { SegmentedControl, showError, Toggle } from '@feathr/components';
import { useCampaignPermissions, useLicense } from '@feathr/extender/hooks';
import { useAccount, useFlags, useUsageAndQuota, useUser } from '@feathr/extender/state';

import CampaignTypeSelectSections from './CampaignTypeSelectSections';
import PixlPlusCard from './PixlPlusCard';

import * as styles from './CampaignTypeSelect.css';

type TCampaignCategory = 'marketing' | 'monetization';
export type TCampaignSection = 'expand' | 'cultivate' | 'understand';

export interface ICampaignTypeSelectProps {
  campaignClasses?: CampaignClass[];
  campaignGroup?: TCampaignGroup;
  defaultCategory?: TCampaignCategory;
  inFlight?: boolean;
  onSelect: (type: CampaignClass, isMonetization?: boolean) => void;
}

export interface IType {
  id: CampaignClass;
  category: TCampaignCategory[];
  sections?: TCampaignSection[];
  usageText?: string | null;
  inFlight?: boolean;
}

const types: IType[] = [
  // Cultivate and Activate

  {
    // Retargeting
    id: CampaignClass.Segment,
    category: ['marketing', 'monetization'],
    sections: ['cultivate'],
  },
  {
    // Mailrail Single Send
    id: CampaignClass.MailrailSingleSend,
    category: ['marketing'],
    sections: ['cultivate'],
  },
  {
    // Mailrail Auto Send
    id: CampaignClass.MailrailAutoSend,
    category: ['marketing'],
    sections: ['cultivate'],
  },
  {
    // Mailrail Drip
    id: CampaignClass.MailrailDrip,
    category: ['marketing'],
    sections: ['cultivate'],
  },
  {
    // Single Send
    id: CampaignClass.PinpointEmail,
    category: ['marketing'],
    sections: ['cultivate'],
  },
  {
    // Auto Send
    id: CampaignClass.AutoPinpointEmail,
    category: ['marketing'],
    sections: ['cultivate'],
  },
  {
    // Smart Send
    id: CampaignClass.SmartPinpointEmail,
    category: ['marketing'],
    sections: ['cultivate'],
  },
  {
    // Drip
    id: CampaignClass.Drip,
    category: ['marketing'],
    sections: ['cultivate'],
  },
  {
    // Meta Retargeting
    id: CampaignClass.Facebook,
    category: ['marketing'],
    sections: ['cultivate'],
  },
  {
    // Meta Email Mapping
    id: CampaignClass.EmailListFacebook,
    category: ['marketing'],
    sections: ['cultivate'],
  },
  {
    // Email Mapping
    id: CampaignClass.EmailList,
    category: ['marketing', 'monetization'],
    sections: ['cultivate'],
  },

  // Understand

  {
    // Tracked Link
    id: CampaignClass.TrackedLink,
    category: ['marketing'],
    sections: ['understand'],
  },
  {
    // Landing Page
    id: CampaignClass.LandingPage,
    category: ['marketing'],
    sections: ['understand'],
  },
  {
    // Conversation
    id: CampaignClass.Conversation,
    category: ['marketing'],
    sections: ['understand'],
  },

  // Expand

  {
    // Mobile Geofencing
    id: CampaignClass.MobileGeoFencing,
    category: ['marketing', 'monetization'],
    sections: ['expand'],
  },
  {
    // Historical Geofencing
    id: CampaignClass.MobileGeoFenceRetargeting,
    category: ['marketing', 'monetization'],
    sections: ['expand'],
  },
  {
    // Search Keyword
    id: CampaignClass.Search,
    category: ['marketing', 'monetization'],
    sections: ['expand'],
  },
  {
    // Lookalike
    id: CampaignClass.SeedSegment,
    category: ['marketing', 'monetization'],
    sections: ['expand'],
  },
  {
    // Affinity
    id: CampaignClass.Affinity,
    category: ['marketing', 'monetization'],
    sections: ['expand'],
  },
  {
    // Invites
    id: CampaignClass.Referral,
    category: ['marketing'],
    sections: ['expand'],
  },
  {
    // Google Ad Grants
    id: CampaignClass.GoogleAdsSmart,
    category: ['marketing'],
    sections: ['expand'],
  },
];

export function campaignSectionTitle(section: TCampaignSection, t: TFunction): string {
  const titles: Record<TCampaignSection, string> = {
    expand: t('Expand'),
    cultivate: t('Cultivate and Activate'),
    understand: t('Understand'),
  };
  return titles[section];
}

export function campaignSectionDescription(section: TCampaignSection, t: TFunction): string {
  const titles: Record<TCampaignSection, string> = {
    expand: t(
      'Grow your community with campaigns that help you get in front of new, qualified audiences.',
    ),
    cultivate: t(
      'Nurture your community and drive impact with campaigns designed to engage and convert people you already know.',
    ),
    understand: t('Gather insights on the behavior, interests, and preferences of your community.'),
  };
  return titles[section];
}

function CampaignTypeSelect({
  campaignClasses = [],
  campaignGroup,
  defaultCategory = 'marketing',
  inFlight = false,
  onSelect,
}: Readonly<ICampaignTypeSelectProps>): JSX.Element {
  const account = useAccount();
  const user = useUser();
  const flags = useFlags();
  const { t } = useTranslation();
  const { getUsageText } = useUsageAndQuota(account.id, [
    EUsageMetrics.EmailMappingCampaigns,
    EUsageMetrics.GeofencingCampaigns,
    EUsageMetrics.HistoricalGeofencingCampaigns,
  ]);
  const license = useLicense();
  const onlyShowAvailableOptions = user.getSetting('only_show_available_options', false);
  const [hideLocked, setHideLocked] = useState(onlyShowAvailableOptions);
  const { omni: showPixlPlus } = useCampaignPermissions();
  const pixlPlusExcludedGroups: TCampaignGroup[] = ['email', 'google-ads', 'other'];
  const showPixlPlusCard =
    showPixlPlus && (!campaignGroup || !pixlPlusExcludedGroups.includes(campaignGroup));

  function getCampaignEnabled(type: CampaignClass): boolean {
    return license[type];
  }

  const [category, setCategory] = useState<TCampaignCategory>(defaultCategory);

  if (campaignClasses.length === 0) {
    types.forEach((type) => {
      if (hideLocked) {
        const enabled = getCampaignEnabled(type.id);
        if (enabled) {
          campaignClasses.push(type.id);
        }
      } else {
        campaignClasses.push(type.id);
      }
    });
  }

  const applyFlightRestrictions = (type: IType, inFlight: boolean): IType => {
    if (type.id === CampaignClass.Drip) {
      return {
        ...type,
        inFlight: inFlight || undefined,
      };
    }
    return type;
  };

  const filteredTypes = types
    .map((type) => applyFlightRestrictions(type, inFlight))
    .filter((type) => {
      switch (type.id) {
        case CampaignClass.EmailList:
          type.usageText = getUsageText(EUsageMetrics.EmailMappingCampaigns);
          break;

        case CampaignClass.MobileGeoFencing:
          type.usageText = getUsageText(EUsageMetrics.GeofencingCampaigns);
          break;

        case CampaignClass.MobileGeoFenceRetargeting:
          type.usageText = getUsageText(EUsageMetrics.HistoricalGeofencingCampaigns);
          break;

        default:
      }
      return !!account;
    })
    .filter((type) => campaignClasses.find((cls) => cls === type.id))
    // TODO: remove this filter once pinpoint campaigns are fully deprecated
    .filter((type) => {
      if (flags.mailrail) {
        return ![
          CampaignClass.PinpointEmail,
          CampaignClass.SmartPinpointEmail,
          CampaignClass.AutoPinpointEmail,
          CampaignClass.Drip,
        ].includes(type.id);
      }
      return ![
        CampaignClass.MailrailSingleSend,
        CampaignClass.MailrailAutoSend,
        CampaignClass.MailrailDrip,
      ].includes(type.id);
    })
    .filter((type) => {
      if (campaignGroup) {
        return CampaignGroupMap[campaignGroup].includes(type.id);
      }
      return true;
    });

  function handleMonetizationSelect(type: CampaignClass): void {
    onSelect(type, true);
  }

  function handleOnChangeTab(value: string): void {
    setCategory(value as TCampaignCategory);
  }

  async function handleChangeShowLocked(value?: boolean): Promise<void> {
    setHideLocked(value ?? false);
    user.setSetting('only_show_available_options', value ?? false);
    try {
      await user.patchDirty();
    } catch (error) {
      showError(error, t);
    }
  }

  const hasHeaderBar =
    (defaultCategory === 'marketing' && (campaignGroup === 'all' || !campaignGroup)) ||
    account.isPlatformLite;

  return (
    <div className={styles.root}>
      {/* Hide tabs when creating from partners page; defaultPage is monetization */}
      {hasHeaderBar && (
        <div className={styles.header}>
          {defaultCategory === 'marketing' && (campaignGroup === 'all' || !campaignGroup) && (
            <SegmentedControl
              data={[
                { label: 'Marketing', value: 'marketing' },
                {
                  label: 'Monetization',
                  value: 'monetization',
                  locked: inFlight || !license.hasMonetization,
                },
              ]}
              name={'add-campaign-modal-segmented-control'}
              onChange={handleOnChangeTab}
              value={category}
            />
          )}
          {account.isPlatformLite && (
            <Toggle
              label={t('Only show available options')}
              onChange={handleChangeShowLocked}
              value={hideLocked}
            />
          )}
        </div>
      )}
      {showPixlPlusCard && (
        <PixlPlusCard onClick={category === 'monetization' ? handleMonetizationSelect : onSelect} />
      )}
      {category === 'marketing' && (
        <CampaignTypeSelectSections
          onSelect={onSelect}
          types={filteredTypes.filter((type) => type.category.includes('marketing'))}
        />
      )}
      {category === 'monetization' && (
        <CampaignTypeSelectSections
          hasSections={false}
          onSelect={handleMonetizationSelect}
          types={filteredTypes.filter((type) => type.category.includes('monetization'))}
        />
      )}
    </div>
  );
}

export default observer(CampaignTypeSelect);
